@tailwind base;
@tailwind components;
@tailwind utilities;

@font-face {
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  src: url("./fonts/Inter-Regular.ttf") format("truetype");
}

@font-face {
  font-family: "Inter";
  font-style: normal;
  font-weight: 500;
  src: url("./fonts/Inter-Medium.ttf") format("truetype");
}

@font-face {
  font-family: "Inter";
  font-style: normal;
  font-weight: 600;
  src: url("./fonts/Inter-SemiBold.ttf") format("truetype");
}

@font-face {
  font-family: "Inter";
  font-style: normal;
  font-weight: 700;
  src: url("./fonts/Inter-Bold.ttf") format("truetype");
}

@layer base {
  html {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  html,
  body,
  #root {
    height: 100%;
  }
  body {
    font-family: theme("fontFamily.sans");
    background: theme("colors.datahub.gray.1600");
  }
  h1,
  h2,
  h3 {
    font-family: theme("fontFamily.display");
    font-weight: 600;
  }
  :focus-visible {
    outline: 2px solid theme("colors.litmus-light-green.500");
    outline-offset: 2px;
    border-radius: 4px;
  }
  [data-num] {
    font-variant-numeric: tabular-nums;
  }
}

@layer components {
  .page-container {
    @apply w-full max-w-[1440px] mx-auto px-12 pb-16;
  }
  .card {
    @apply rounded-navbar border border-datahub-gray-100 bg-surface shadow-navbar;
  }
  .card-hover {
    @apply transition-all duration-200 hover:shadow-navbar;
  }

  .btn {
    @apply inline-flex items-center justify-center gap-2 rounded-md px-3.5 py-2 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50;
  }
  .btn-primary {
    @apply btn bg-litmus-dark-green-700 text-white hover:bg-litmus-dark-green-600;
  }
  .btn-secondary {
    @apply btn border border-datahub-gray-100 bg-surface text-datahub-gray-600 hover:bg-datahub-gray-1500;
  }
  .btn-ghost {
    @apply btn text-datahub-gray-1700 hover:bg-datahub-gray-1500 hover:text-datahub-gray-600;
  }
  .btn-danger {
    @apply btn text-rose-700 hover:bg-rose-50;
  }

  .btn-primary-outline {
    @apply btn border border-litmus-dark-green-700/50 bg-transparent text-litmus-dark-green-700;
  }

  .btn-primary-outline:hover {
    @apply bg-litmus-light-green-100 text-litmus-dark-green-900;
  }

  .btn-secondary-outline {
    @apply btn border border-datahub-gray-100 bg-transparent text-datahub-gray-600;
  }

  .btn-secondary-outline:hover {
    @apply bg-datahub-gray-1500 text-datahub-gray-600;
  }

  .btn-ghost-outline {
    @apply btn border border-datahub-gray-300 bg-transparent text-datahub-gray-1700;
  }

  .btn-ghost-outline:hover {
    @apply bg-datahub-gray-1500 text-datahub-gray-600;
  }

  .btn-danger-outline {
    @apply btn border border-rose-600 bg-transparent text-rose-700;
  }

  .btn-danger-outline:hover {
    @apply bg-rose-600 text-white;
  }

  /* Input primitive. */
  .input {
    @apply w-full rounded-md border border-datahub-gray-100 bg-surface px-3 py-2 text-sm text-datahub-gray-600 shadow-sm transition-colors placeholder:text-datahub-gray-1800 hover:border-datahub-gray-1800 focus:border-litmus-light-green-300 focus:outline-none focus:ring-1 focus:ring-litmus-light-green-300;
  }

  /* Time input: make the editable text region show a text cursor, and isolate
     cursor: pointer strictly to the clock-icon button so users can tell the
     two interaction zones apart (WCAG 1.4.13, pointer affordance). */
  input[type="time"] {
    cursor: text;
  }
  input[type="time"]::-webkit-calendar-picker-indicator {
    cursor: pointer;
    border-radius: 3px;
    padding: 2px;
    opacity: 0.55;
    transition: opacity 150ms;
  }
  input[type="time"]::-webkit-calendar-picker-indicator:hover {
    opacity: 1;
  }

  /* Status chips. */
  .chip {
    @apply inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider;
  }
  .chip-emerald {
    @apply chip border-emerald-500/30 bg-emerald-50 text-emerald-700;
  }
  .chip-amber {
    @apply chip border-amber-500/30 bg-amber-50 text-amber-700;
  }
  .chip-rose {
    @apply chip border-rose-500/30 bg-rose-50 text-rose-700;
  }
  .chip-neutral {
    @apply chip border-datahub-gray-100 bg-datahub-gray-1500 text-datahub-gray-1700;
  }
  .chip-brand {
    @apply chip border-litmus-light-green-300 bg-litmus-light-green-100 text-litmus-light-green-800;
  }

  .eyebrow {
    @apply font-sans text-[11px] font-bold uppercase text-datahub-gray-1800;
  }

  /* Suppress Edge's native password reveal icon — we provide our own toggle */
  input[type="password"]::-ms-reveal {
    display: none;
  }

  /* no scrollbar modal */
  .no-scrollbar {
    -ms-overflow-style: none; /* IE + Edge */
    scrollbar-width: none; /* Firefox */
  }

  .no-scrollbar::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }

  .nav-sidebar {
    @apply fixed left-0 z-20 flex flex-col overflow-y-auto overflow-x-visible bg-litmus-dark-green-900;
    overscroll-behavior: contain;
    border-right: 1px solid rgba(58, 219, 144, 0.3);
  }

  .nav-brand {
    @apply flex shrink-0 cursor-pointer items-center justify-center gap-2.5 overflow-hidden px-4 py-3;
  }

  .nav-brand--collapsed {
    @apply px-0;
  }

  .nav-brand-logo {
    @apply relative flex h-9 shrink-0 items-center justify-center overflow-hidden;
    width: 138px;
    transition: width 250ms ease-in-out;
  }

  .nav-brand-logo--collapsed {
    width: 36px;
  }

  .nav-brand-full {
    @apply absolute h-9 w-auto object-contain;
    transition:
      opacity 200ms ease-in-out,
      transform 200ms ease-in-out;
    opacity: 1;
    transform: scale(1);
  }

  .nav-brand-icon {
    @apply absolute h-7 w-7 object-contain;
    transition:
      opacity 200ms ease-in-out,
      transform 200ms ease-in-out;
    opacity: 0;
    transform: scale(0.9);
  }

  .nav-brand-logo--collapsed .nav-brand-full {
    opacity: 0;
    transform: scale(0.9);
    pointer-events: none;
  }

  .nav-brand-logo--collapsed .nav-brand-icon {
    opacity: 1;
    transform: scale(1);
  }

  .nav-menu {
    @apply flex min-h-0 flex-1 flex-col overflow-y-auto;
  }

  .nav-menu-item {
    @apply relative my-1 flex h-9 min-h-9 w-full items-center gap-2.5 rounded-lg border-0 px-2.5 text-left text-[14px] font-semibold text-[#C8CDD5] transition-colors duration-200;
  }

  .nav-menu-item:hover {
    background: rgba(255, 255, 255, 0.06);
  }

  .nav-menu-item-selected {
    @apply font-bold;
    background: #3adb90 !important;
    color: #fff;
  }

  .nav-menu-item-collapsed {
    @apply w-9 justify-center px-0;
  }

  .nav-menu-icon {
    @apply flex h-5 w-5 shrink-0 items-center justify-center;
  }

  .top-nav-icon-button {
    @apply inline-flex h-9 min-w-9 cursor-pointer items-center justify-center rounded-lg border border-white/20 bg-white/10 px-2.5 text-white transition-colors duration-150;
  }

  .top-nav-icon-button:hover {
    background: rgba(255, 255, 255, 0.14);
  }

  .top-nav-avatar {
    @apply relative flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-2 border-white/10 bg-transparent p-0 transition-all duration-200;
  }

  .top-nav-avatar:hover {
    @apply border-white/40;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .top-nav-avatar span {
    @apply flex h-8 w-8 items-center justify-center rounded-full bg-litmus-light-green-100 text-[14px] font-bold uppercase text-litmus-dark-green;
    box-shadow:
      inset 0 1px 2px rgba(255, 255, 255, 0.3),
      0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .user-dropdown {
    @apply fixed right-[18px] top-[60px] z-[3000] w-[200px] rounded-lg bg-white py-1.5 text-[#222];
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
  }

  .user-dropdown-item {
    @apply flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#222];
  }

  .user-dropdown-item:hover {
    @apply bg-[#f5f5f5];
  }

  .user-dropdown-item svg {
    @apply h-4 w-4;
  }

  .drawer-backdrop {
    @apply pointer-events-none fixed left-0 z-20 w-screen bg-black/40 opacity-0 transition-opacity duration-200;
  }

  .drawer-backdrop-visible {
    @apply pointer-events-auto opacity-100;
  }

  .sidebar-boundary-toggle {
    @apply pointer-events-auto fixed top-4 z-40 flex h-5 w-5 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-litmus-dark-green-700 text-litmus-light-green-300 transition-colors duration-200;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  }

  .sidebar-boundary-toggle:hover {
    @apply bg-litmus-light-green-500 text-litmus-dark-green-900;
  }

  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: theme("colors.datahub.gray.100");
    border-radius: 999px;
    border: 2px solid theme("colors.datahub.gray.1600");
  }
  ::-webkit-scrollbar-thumb:hover {
    background-color: theme("colors.datahub.gray.1800");
  }
}

@layer utilities {
  /* The dashboard hero uses a faint grid + radial mask to fade the texture
     into the background. */
  .grid-hero {
    background-image: theme("backgroundImage.grid");
    background-size: theme("backgroundSize.grid");
    -webkit-mask-image: radial-gradient(
      ellipse 80% 60% at 50% 0%,
      black 30%,
      transparent 75%
    );
    mask-image: radial-gradient(
      ellipse 80% 60% at 50% 0%,
      black 30%,
      transparent 75%
    );
  }
}
